import {
  buildAdvancedPlayerStats,
  extractShotXg,
  estimateShotXg,
  isShotOnTarget,
  resolvePlayerName,
} from './gameStatsHelpers.js';

const TEAM_KEYS = new Set(['red', 'blue']);

const lower = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const finite = (value) => {
  // Parsed replay records use explicit nulls for unavailable tick/coordinate
  // fields. Treat those as missing instead of coercing null to numeric zero;
  // otherwise a later, valid matchTick is hidden and every event renders at
  // 0:00.
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export function eventTick(event) {
  if (!event || typeof event !== 'object') return null;
  const candidates = [
    event.replayTick,
    event.matchTick,
    event.gameTicks,
    event.tick,
    event.clockTicks,
    event.shot?.replayTick,
    event.shot?.matchTick,
    event.shot?.tick,
    event.lastShot?.replayTick,
    event.lastShot?.matchTick,
    event.lastShot?.tick,
  ];
  let zero = null;
  for (const candidate of candidates) {
    const value = finite(candidate);
    if (value === null) continue;
    // Older sidecars sometimes wrote replayTick as zero while retaining the
    // real match or shot tick. Keep zero only as a last-resort value so those
    // events do not all collapse onto 0:00.
    if (value === 0) {
      if (zero === null) zero = value;
      continue;
    }
    return value;
  }
  return zero;
}

export function eventSeconds(event, match = null) {
  // The profile's clock must match the replay seek position. Match-clock
  // seconds can pause/reset around goals, while replayTick is the native
  // renderer's authoritative timeline.
  const replayTick = finite(event?.replayTick);
  if (replayTick !== null && replayTick > 0) return Math.max(0, replayTick / 60);
  const fallbackTick = eventTick(event);
  if (fallbackTick !== null && fallbackTick > 0) return Math.max(0, fallbackTick / 60);
  const direct = finite(event?.clockSeconds);
  if (direct !== null) return Math.max(0, direct);
  if (fallbackTick !== null) return Math.max(0, fallbackTick / 60);
  const duration = finite(match?.gameTicks);
  return duration !== null ? duration / 60 : null;
}

export function formatInsightTime(value) {
  const seconds = Math.max(0, finite(value) ?? 0);
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${rest}`;
}

export function normalizeInsightTeam(value) {
  const team = lower(value);
  return TEAM_KEYS.has(team) ? team : null;
}

export function playerTeam(match, player) {
  const key = lower(player);
  if (!key || !match) return null;
  const inList = (list) => Array.isArray(list) && list.some((entry) => lower(resolvePlayerName(entry)) === key);
  if (inList(match.redTeam)) return 'red';
  if (inList(match.blueTeam)) return 'blue';
  const record = (Array.isArray(match.player) ? match.player : []).find((entry) => lower(resolvePlayerName(entry)) === key);
  return normalizeInsightTeam(record?.team);
}

function eventPosition(event) {
  const position = event?.from || event?.position || event?.ball || event;
  const x = finite(position?.x);
  const y = finite(position?.y);
  return x === null || y === null ? null : { x, y };
}

function eventPlayer(event) {
  return resolvePlayerName(event?.player ?? event?.scorer ?? event?.nick ?? event?.keeper ?? event?.name ?? event?.by);
}

function samePlayer(left, right) {
  return lower(resolvePlayerName(left)) === lower(resolvePlayerName(right));
}

function sameTeam(left, right) {
  const a = normalizeInsightTeam(left);
  const b = normalizeInsightTeam(right);
  return !!a && !!b && a === b;
}

function eventBase({ type, label, detail, event, match, team, position, tone = 'neutral', value = null, related = null }) {
  const tick = eventTick(event);
  const resolvedDetail = typeof detail === 'function' ? detail(event) : detail;
  const resolvedTone = typeof tone === 'function' ? tone(event) : tone;
  return {
    id: `${type}-${tick ?? 'unknown'}-${label}`,
    type,
    label,
    detail: resolvedDetail || '',
    tick,
    seconds: eventSeconds(event, match),
    timeLabel: formatInsightTime(eventSeconds(event, match)),
    team: normalizeInsightTeam(team),
    position: position || eventPosition(event),
    tone: resolvedTone || 'neutral',
    value: finite(value),
    related: related || null,
    player: eventPlayer(event),
  };
}

function shotXg(match, shot) {
  const recorded = finite(extractShotXg(shot));
  if (recorded !== null && recorded > 0) return recorded;
  const estimated = estimateShotXg(shot, {
    stadium: match?.stadium,
    redTeam: match?.redTeam,
    blueTeam: match?.blueTeam,
  });
  return finite(estimated) ?? 0;
}

function shotOutcome(shot) {
  const outcome = String(shot?.outcome || '').toLowerCase();
  if (shot?.goal === true || shot?.scored === true || outcome === 'goal') return 'goal';
  if (['saved', 'save', 'blocked', 'block', 'parried', 'deflect', 'rebound'].includes(outcome) || shot?.savedBy) return 'saved';
  if (isShotOnTarget(shot)) return 'on_target';
  return 'off_target';
}

function assistPlayer(goal) {
  return resolvePlayerName(goal?.assist?.player ?? goal?.assist?.nick ?? goal?.assistName ?? goal?.assist_player ?? goal?.assist);
}

function isPlayerInPossession(possession, player) {
  if (!possession?.players || typeof possession.players.has !== 'function') return false;
  return possession.players.has(player) || Array.from(possession.players.keys()).some((name) => samePlayer(name, player));
}

function possessionPlayers(possession) {
  // Preserve returns to the same player (A → B → A). The summary map contains
  // unique participants and cannot represent the order of a passing move.
  if (Array.isArray(possession?.playerSequence)) return possession.playerSequence;
  if (!possession?.players || typeof possession.players.keys !== 'function') return [];
  return Array.from(possession.players.keys()).filter(Boolean);
}

function sortEvents(events) {
  return events.sort((a, b) => {
    const at = a.tick === null ? Number.POSITIVE_INFINITY : a.tick;
    const bt = b.tick === null ? Number.POSITIVE_INFINITY : b.tick;
    return at - bt || a.label.localeCompare(b.label);
  });
}

function buildShotEvents(match, player = null) {
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  return shots.reduce((events, shot, index) => {
    const shooter = eventPlayer(shot);
    if (!shooter || (player && !samePlayer(shooter, player))) return events;
    const xg = shotXg(match, shot);
    const outcome = shotOutcome(shot);
    const label = outcome === 'goal' ? 'Goal' : outcome === 'saved' ? 'Shot saved' : outcome === 'on_target' ? 'Shot on target' : 'Shot missed';
    const tone = outcome === 'goal' ? 'positive' : outcome === 'off_target' ? (xg >= 0.45 ? 'negative' : 'neutral') : 'neutral';
    const origin = eventPosition(shot.from || shot);
    const target = eventPosition(shot.to || shot.target || shot.result?.to);
    events.push(eventBase({
      type: 'shot',
      label,
      detail: `xG ${xg.toFixed(2)} · ${outcome.replace('_', ' ')}`,
      event: shot,
      match,
      team: shot.team,
      position: origin,
      tone,
      value: xg,
      related: { shotIndex: index, outcome, xg, from: origin, to: target },
    }));
    return events;
  }, []);
}

function buildAssistAndPassEvents(match, player = null) {
  const passes = Array.isArray(match?.passes) ? match.passes : [];
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  const events = [];
  passes.forEach((pass, index) => {
    const passer = typeof pass === 'string' ? pass : eventPlayer(pass);
    if (!passer || (player && !samePlayer(passer, player))) return;
    // A completed pass includes both the release (lastShot) and reception
    // (shot). Its timeline link belongs to the player making the pass.
    const passEvent = eventTick(pass?.lastShot) !== null ? pass.lastShot : pass;
    const passTick = eventTick(passEvent);
    const passTeam = normalizeInsightTeam(pass?.team ?? pass?.lastShot?.team);
    const linkedShotIndex = shots.findIndex((shot) => {
      if (!shot || samePlayer(eventPlayer(shot), passer)) return false;
      if (pass?.shot && shot === pass.shot) return true;
      const shotTick = eventTick(shot);
      const shotTeam = normalizeInsightTeam(shot.team);
      return passTick !== null && shotTick !== null && shotTick >= passTick && shotTick - passTick <= 240 && sameTeam(passTeam, shotTeam);
    });
    const linkedShot = linkedShotIndex >= 0 ? shots[linkedShotIndex] : null;
    const explicitKey = linkedShot && (
      samePlayer(linkedShot.assist, passer)
      || samePlayer(linkedShot.assist?.player, passer)
    );
    const keyPass = !!linkedShot;
    const xg = linkedShot ? shotXg(match, linkedShot) : null;
    const direction = typeof pass?.direction === 'string' ? pass.direction.toLowerCase() : null;
    const detail = keyPass
      ? `Created a ${xg === null ? '' : `${xg.toFixed(2)} xG `}shot${explicitKey ? ' · linked assist' : ''}`
      : (direction ? `${direction} pass` : 'Completed pass');
    events.push(eventBase({
      type: keyPass ? 'key-pass' : 'pass',
      label: keyPass ? 'Key pass' : 'Pass',
      detail,
      event: passEvent,
      match,
      team: passTeam,
      position: eventPosition(pass?.lastShot || pass?.shot || pass),
      tone: keyPass ? 'positive' : 'neutral',
      value: xg,
      related: { passIndex: index, shotIndex: linkedShotIndex >= 0 ? linkedShotIndex : null, direction },
    }));
  });
  return events;
}

function buildGoalEvents(match, player = null) {
  const goals = Array.isArray(match?.goals) ? match.goals : [];
  const events = [];
  goals.forEach((goal, index) => {
    const scorer = resolvePlayerName(goal?.scorer);
    const assist = assistPlayer(goal);
    const scorerMatch = scorer && (!player || samePlayer(scorer.replace(/\s*\(own goal\)$/i, ''), player));
    const assistMatch = assist && (!player || samePlayer(assist, player));
    if (!scorerMatch && !assistMatch) return;
    const ownGoal = /\(own goal\)$/i.test(scorer || '') || goal?.ownGoal === true;
    const role = scorerMatch ? (ownGoal ? 'Own goal' : 'Goal') : 'Assist';
    const assistTick = assistMatch && !scorerMatch ? eventTick(goal?.assist?.shot || goal?.assist) : null;
    const event = assistTick !== null ? { ...goal, replayTick: assistTick } : goal;
    events.push(eventBase({
      type: scorerMatch && !ownGoal ? 'goal' : scorerMatch ? 'own-goal' : 'assist',
      label: role,
      detail: scorerMatch ? `Scored by ${scorer || 'player'}` : `Created for ${scorer || 'teammate'}`,
      event,
      match,
      team: goal?.for ?? goal?.team,
      position: eventPosition(goal?.ballCoord || goal?.shot || goal),
      tone: scorerMatch && ownGoal ? 'negative' : 'positive',
      related: { goalIndex: index, scorer, assist, assistTick },
    }));
  });
  return events;
}

function buildOtherEvents(match, player = null) {
  const events = [];
  const pushList = (list, options) => {
    (Array.isArray(list) ? list : []).forEach((item, index) => {
      const owner = options.owner(item);
      if (!owner || (player && !samePlayer(owner, player))) return;
      events.push(eventBase({ ...options, event: item, match, team: options.team?.(item) ?? item?.team, position: options.position?.(item), related: { index } }));
    });
  };
  pushList(match?.saves, {
    type: 'save', label: 'Save', detail: (item) => `Saved by ${eventPlayer(item) || 'keeper'}`,
    owner: (item) => eventPlayer(item),
    position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.duels, {
    type: 'duel', label: 'Duel', owner: (item) => {
      if (!player) return eventPlayer(item);
      const winner = resolvePlayerName(item?.winner || item?.winnerPlayer || item?.player);
      const loser = resolvePlayerName(item?.loser || item?.loserPlayer || item?.opponent);
      return samePlayer(winner, player) ? winner : (samePlayer(loser, player) ? loser : null);
    },
    detail: (item) => {
      const winner = resolvePlayerName(item?.winner || item?.winnerPlayer || item?.player);
      const loser = resolvePlayerName(item?.loser || item?.loserPlayer || item?.opponent);
      return !player || samePlayer(winner, player)
        ? `Won vs ${loser || 'opponent'}`
        : `Lost vs ${winner || 'opponent'}`;
    },
    position: (item) => eventPosition(item), tone: (item) => {
      const winner = resolvePlayerName(item?.winner || item?.winnerPlayer || item?.player);
      return !player || samePlayer(winner, player) ? 'positive' : 'negative';
    },
  });
  pushList(match?.clearances, {
    type: 'clearance', label: 'Clearance', owner: (item) => eventPlayer(item), detail: () => 'Defensive clearance', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.clearanceRecoveries, {
    type: 'recovery', label: 'Recovery', owner: (item) => eventPlayer(item), detail: () => 'Recovered the ball after a clearance', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.rebounds, {
    type: 'recovery', label: 'Rebound recovery', owner: (item) => eventPlayer(item), detail: () => 'Recovered a rebound', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.interceptions, {
    type: 'interception', label: 'Interception', owner: (item) => eventPlayer(item), detail: () => 'Won the ball by reading a pass', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.blocks, {
    type: 'block', label: 'Block', owner: (item) => eventPlayer(item), detail: () => 'Blocked an opponent attempt', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.tackles, {
    type: 'tackle', label: 'Tackle', owner: (item) => eventPlayer(item), detail: () => 'Stopped the opponent in possession', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  pushList(match?.sweeperActions, {
    type: 'sweeper', label: 'Sweeper action', owner: (item) => eventPlayer(item), detail: () => 'Swept up danger behind the line', position: (item) => eventPosition(item), tone: () => 'positive',
  });
  return events;
}

function isKeeperPlayer(match, player, rating, summary) {
  const values = [rating?.role, rating?.position, summary?.role, summary?.position, summary?.metrics?.position];
  if (values.some((value) => /(^|[^a-z])(gk|keeper|goalie|goalkeeper)([^a-z]|$)/i.test(String(value || '')))) return true;
  return Number(summary?.metrics?.gkTicks) > 0;
}

function buildSaveMap(match, player, team, isKeeper) {
  if (!isKeeper) return [];
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  const saves = (Array.isArray(match?.saves) ? match.saves : [])
    .filter((save) => samePlayer(eventPlayer(save), player))
    .map((save, index) => {
      const shot = shots[finite(save?.shotIndex) ?? -1] || null;
      const from = eventPosition(shot?.from || shot || save);
      const to = eventPosition(shot?.to || shot?.target || save?.position || save);
      return {
        ...eventBase({ type: 'save', label: 'Save', detail: 'Stopped an opponent shot', event: save, match, team: save?.team || team, position: from, tone: 'positive' }),
        id: `save-map-${eventTick(save) ?? 'unknown'}-${index}`,
        markerTone: 'save',
        color: '#22c55e',
        related: { from, to, kind: 'save' },
      };
    });
  const conceded = shots.reduce((entries, shot, index) => {
    const shooterTeam = normalizeInsightTeam(shot?.team);
    if (!shooterTeam || shooterTeam === team || shotOutcome(shot) !== 'goal') return entries;
    const from = eventPosition(shot?.from || shot);
    const to = eventPosition(shot?.to || shot?.target || shot);
    entries.push({
      ...eventBase({ type: 'conceded-goal', label: 'Goal conceded', detail: 'Opponent goal', event: shot, match, team: shooterTeam, position: from, tone: 'negative' }),
      id: `conceded-goal-${eventTick(shot) ?? 'unknown'}-${index}`,
      markerTone: 'conceded',
      color: '#ef4444',
      goal: true,
      related: { from, to, kind: 'conceded-goal' },
    });
    return entries;
  }, []);
  return sortEvents([...saves, ...conceded]);
}

export function buildMatchKeyMoments(match) {
  if (!match) return [];
  const important = [
    ...buildGoalEvents(match),
    ...buildShotEvents(match).filter((event) => event.type === 'shot' && (event.related.outcome === 'goal' || event.related.outcome === 'saved' || event.related.xg >= 0.4)),
    ...buildOtherEvents(match).filter((event) => ['save', 'duel', 'clearance', 'recovery'].includes(event.type)),
  ];
  const unique = new Map();
  important.forEach((event) => {
    const key = `${event.type}:${lower(event.related?.scorer || event.related?.player || event.label)}:${event.tick ?? event.id}`;
    if (!unique.has(key)) unique.set(key, event);
  });
  return sortEvents(Array.from(unique.values())).slice(0, 160);
}

export function buildPlayerInsight(match, player, rating = null) {
  const nick = resolvePlayerName(player) || '';
  const team = playerTeam(match, nick);
  const advanced = buildAdvancedPlayerStats(match || {});
  const possessions = Array.isArray(advanced?.possessions) ? advanced.possessions : [];
  const playerPossessions = possessions.filter((possession) => isPlayerInPossession(possession, nick));
  const summary = advanced?.advanced?.get?.(nick) || advanced?.advanced?.get?.(resolvePlayerName(player));
  const isKeeper = isKeeperPlayer(match, nick, rating, summary);
  const events = sortEvents([
    ...buildGoalEvents(match, nick),
    ...buildShotEvents(match, nick),
    ...buildAssistAndPassEvents(match, nick),
    ...buildOtherEvents(match, nick),
  ]);
  const negativeEvents = events.filter((event) => event.tone === 'negative');
  const shotEvents = events.filter((event) => event.type === 'shot');
  const shotMap = shotEvents.map((event) => ({ ...event, xg: event.related?.xg ?? event.value ?? 0, outcome: event.related?.outcome || 'unknown' }));
  const timeline = events;
  const keyPasses = events.filter((event) => event.type === 'key-pass');
  const duels = events.filter((event) => event.type === 'duel');
  const defensiveActions = events.filter((event) => ['duel', 'clearance', 'recovery', 'save', 'interception', 'block', 'tackle', 'sweeper'].includes(event.type));
  const saveMap = buildSaveMap(match, nick, team, isKeeper);
  const counts = {
    goals: events.filter((event) => event.type === 'goal').length,
    assists: events.filter((event) => event.type === 'assist').length,
    shots: shotEvents.length,
    shotsOnTarget: shotEvents.filter((event) => ['goal', 'saved', 'on_target'].includes(event.related?.outcome)).length,
    xg: shotMap.reduce((sum, event) => sum + (finite(event.xg) || 0), 0),
    keyPasses: events.filter((event) => event.type === 'key-pass').length,
    passes: events.filter((event) => event.type === 'pass' || event.type === 'key-pass').length,
    saves: events.filter((event) => event.type === 'save').length,
    duelsWon: events.filter((event) => event.type === 'duel' && event.tone === 'positive').length,
    duelsLost: events.filter((event) => event.type === 'duel' && event.tone === 'negative').length,
    recoveries: events.filter((event) => event.type === 'recovery').length,
    clearances: events.filter((event) => event.type === 'clearance').length,
    possessions: playerPossessions.length,
    turnovers: playerPossessions.filter((possession) => possession.result === 'turnover' && samePlayer(possession.lastPlayer, nick)).length,
    dangerousTurnovers: summary?.dangerousTurnovers || 0,
    touches: summary?.possessionTouches || 0,
    progressivePasses: summary?.progressivePasses || 0,
    progressiveDistance: summary?.progressiveDistance || 0,
    finalThirdEntries: summary?.finalThirdEntries || 0,
    counterRuns: summary?.counterRuns || 0,
    forcedTurnovers: summary?.forcedTurnovers || 0,
  };
  const chains = playerPossessions.map((possession, index) => ({
    id: `possession-${index}-${possession.startTick}`,
    focusPlayer: nick,
    team: normalizeInsightTeam(possession.team),
    result: possession.result || 'retained',
    startTick: finite(possession.startReplayTick) ?? finite(possession.startTick),
    endTick: finite(possession.endReplayTick) ?? finite(possession.endTick),
    startSeconds: eventSeconds({ replayTick: possession.startReplayTick, matchTick: possession.startTick }, match),
    endSeconds: eventSeconds({ replayTick: possession.endReplayTick, matchTick: possession.endTick }, match),
    players: possessionPlayers(possession),
    touches: possession.players?.get?.(nick)?.touches || 0,
    events: Array.isArray(possession.ticks) ? possession.ticks.length : 0,
  }));
  return {
    player: nick,
    team,
    rating,
    summary,
    counts,
    timeline,
    keyPasses,
    duels,
    defensiveActions,
    saveMap,
    isKeeper,
    keyMoments: timeline.filter((event) => ['goal', 'assist', 'key-pass', 'save', 'duel', 'shot', 'clearance', 'recovery'].includes(event.type)),
    mistakes: negativeEvents,
    shots: shotMap,
    possessionChains: chains,
    confidence: rating?.breakdown?.confidence ?? null,
  };
}

export function playerPositionAt(playerPos, playerList, nick, tick) {
  if (!Array.isArray(playerPos) || !Array.isArray(playerList)) return null;
  const index = playerList.findIndex((entry) => samePlayer(entry, nick));
  if (index < 0 || !Array.isArray(playerPos[index]) || !playerPos[index].length) return null;
  const target = Math.max(0, Math.min(playerPos[index].length - 1, Math.round(Number(tick) || 0)));
  return playerPos[index][target] || null;
}
